import React, { memo, useCallback } from "react";
import { Platform, Pressable, StyleSheet } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { useTheme } from "styled-components/native";
import { useScrollContext } from "@navigations/utils/ScrollContext";
import { useSelector } from "react-redux";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useNavigation } from "@react-navigation/native";
import Routes from "@navigations/Routes";
import {
  AIEventNames,
  AIEventTypes,
} from "@scenes/authentication/utils/Types/types";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { ChatTooltip } from "./ChatToolTip";
import { AISearchButtonVisual } from "./AISearchButtonVisual";

const isWeb = Platform.OS === "web";

type AISearchFloatingButtonProps = {
  isPositioned?: boolean;
  bottomMargin?: number;
  right?: number;
  isTooltipOpen?: boolean;
  onOpenTooltip?: () => void;
  onCloseTooltip?: () => void;
  tooltipPosition?: { x: number; y: number };
};

const AISearchFloatingButton = memo(
  ({
    isPositioned = true,
    bottomMargin = 110,
    right = 12,
    isTooltipOpen: externalTooltipOpen,
    onOpenTooltip,
    onCloseTooltip,
    tooltipPosition,
  }: AISearchFloatingButtonProps) => {
    const typography = useTypographyStore();
    const theme = useTheme();
    const navigation = useNavigation();
    const [scrollValue] = useScrollContext();
    const { top, bottom } = useSafeAreaInsets();
    const { trackEvent } = useUserAnalytics() || {};

    const [internalTooltipOpen, setInternalTooltipOpen] = React.useState(false);

    const isTooltipOpen =
      externalTooltipOpen !== undefined
        ? externalTooltipOpen
        : internalTooltipOpen;

    const navigationHeight = useSelector(
      (state: any) => state.appState?.navigationHeight
    );
    const height = (navigationHeight || 0) - top;

    const animatedContainerStyle = useAnimatedStyle(() => {
      "worklet";
      if (!isPositioned) {
        return {};
      }
      return {
        transform: [
          {
            translateY: withTiming(
              scrollValue.value > 0.2 ? height + bottom + 20 : 0,
              {
                duration: 550,
                easing: Easing.out(Easing.cubic),
              }
            ),
          },
        ],
      };
    }, [height, bottom, isPositioned]);

    const handlePress = useCallback(() => {
      trackEvent?.(AIEventNames.AI_CHAT_WIDGET, {
        action: AIEventTypes.WIDGET_OPENED,
      });
      if (isWeb) {
        if (onOpenTooltip) {
          onOpenTooltip();
        } else {
          setInternalTooltipOpen(true);
        }
      } else {
        navigation.navigate(Routes.AIChatBotScreen as never);
      }
    }, [navigation, onOpenTooltip]);

    const handleCloseTooltip = useCallback(() => {
      if (onCloseTooltip) {
        onCloseTooltip();
      } else {
        setInternalTooltipOpen(false);
      }
      trackEvent?.(AIEventNames.AI_CHAT_WIDGET, {
        action: AIEventTypes.WIDGET_CLOSED,
      });
    }, [onCloseTooltip]);

    if (!isPositioned) {
      return (
        <AISearchButtonVisual
          typography={
            theme.isLargeScreen ? typography["H3-M"] : typography["Body-M"]
          }
        />
      );
    }

    return (
      <>
        <Animated.View
          style={[
            styles.container,
            { right, bottom: bottomMargin },
            animatedContainerStyle,
          ]}
        >
          <Pressable onPress={handlePress}>
            <AISearchButtonVisual
              typography={
                theme.isLargeScreen ? typography["H3-M"] : typography["Body-M"]
              }
            />
          </Pressable>
        </Animated.View>

        {isWeb && (
          <ChatTooltip
            isOpen={isTooltipOpen}
            onClose={handleCloseTooltip}
            theme={theme}
            position={tooltipPosition}
          />
        )}
      </>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
});

export default AISearchFloatingButton;
